import axios from 'axios';
import { API_BASE_URL, salvarDados, buscarDadosPorId } from './indexedDb';
import { navigateTo } from '../navigate';

const api = axios.create({
  baseURL: API_BASE_URL,
});

// Variáveis para gerenciar a fila de refresh
let isRefreshing = false;
let failedQueue = [];

const processQueue = (error, token = null) => {
  failedQueue.forEach((prom) => {
    if (error) {
      prom.reject(error);
    } else {
      prom.resolve(token);
    }
  });

  failedQueue = [];
};

// Função para obter novo access token
async function refreshAccessToken() {
  const authData = await buscarDadosPorId("local_registros", "auth");
  const refreshToken = authData?.refresh;

  if (!refreshToken) throw new Error("Refresh token ausente");

  // Chamada de refresh usando axios puro para não passar pelos interceptors
  const response = await axios.post(`${API_BASE_URL}/api/token/refresh/`, {
    refresh: refreshToken,
  });

  const newAccess = response.data; // Espera-se { access: "...", refresh: "..." }

  // Salva os novos dados
  await salvarDados("local_registros", {
    ...authData,
    ...response.data,
    access: newAccess.access,
    refresh: newAccess.refresh || refreshToken, // Usa o novo refresh se vier, senão mantém o antigo
    id: "auth"
  });
  
  return newAccess;
}

// Interceptor de requisição
api.interceptors.request.use(async (config) => {
  const authData = await buscarDadosPorId("local_registros", "auth");
  const token = authData?.access;
  
  if (token && config.is_auth) {
    config.headers.Authorization = `Bearer ${token}`;
  }

  return config;
});

// Interceptor de resposta: Renova e Tenta Novamente
api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;

    // Se erro 401 e ainda não tentamos o retry
    if (error.response?.status === 401 && !originalRequest._retry) {
      
      if (isRefreshing) {
        // Se já houver um refresh em curso, adiciona esta requisição à fila
        return new Promise(function(resolve, reject) {
          failedQueue.push({ resolve, reject });
        })
          .then((token) => {
            originalRequest.headers.Authorization = `Bearer ${token}`;
            return api(originalRequest);
          })
          .catch((err) => {
            return Promise.reject(err);
          });
      }

      originalRequest._retry = true;
      isRefreshing = true;

      return new Promise(function (resolve, reject) {
        refreshAccessToken()
          .then((tokenData) => {
            const newAccess = tokenData.access;
            api.defaults.headers.Authorization = `Bearer ${newAccess}`;
            originalRequest.headers.Authorization = `Bearer ${newAccess}`;
            
            processQueue(null, newAccess);
            resolve(api(originalRequest));
          })
          .catch((err) => {
            processQueue(err, null);
            console.error("Falha definitiva na renovação ou na segunda tentativa:", err);
            
            if (err.response?.status === 401 || err.response?.status === 403 || err.message === "Refresh token ausente") {
              navigateTo('/login');
            }
            reject(err);
          })
          .finally(() => {
            isRefreshing = false;
          });
      });
    }

    return Promise.reject(error);
  }
);

export default api;
